import type { Place, PlaceWriteRequest } from '@recipes/contracts';
import { rankPlaceCandidates, type LatLng, type OsmPlaceCandidate } from '@recipes/domain';
import {
  Alert,
  Badge,
  Button,
  Input,
  Label,
  Spinner,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@recipes/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Check, MapPin, Search } from 'lucide-react';
import { useEffect, useId, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '@/lib/api';
import { toastError } from '@/lib/errors';
import { queryKeys } from '@/lib/query-keys';
import { LazyTastingMap } from './map/lazy-map';
import { PlacesLookupError, getPlacesClient } from './places';

function candidateToRequest(c: OsmPlaceCandidate): PlaceWriteRequest {
  return {
    provider: 'OSM',
    providerRef: c.ref,
    name: c.name,
    category: c.category,
    cuisines: c.cuisines,
    lat: c.lat,
    lng: c.lng,
    street: c.street,
    postalCode: c.postalCode,
    city: c.city,
    region: c.region,
    countryCode: c.countryCode,
    website: c.website,
  };
}

function formatDistance(meters: number): string {
  return meters < 1000 ? `${Math.round(meters / 10) * 10} m` : `${(meters / 1000).toFixed(1)} km`;
}

function placeLine(c: { street: string | null; city: string | null; cuisines: string[] }): string {
  return [c.cuisines.slice(0, 3).join(', '), c.street, c.city].filter(Boolean).join(' · ');
}

/** All of the user's places (they rank first and back the search tab). */
function useMyPlaces() {
  return useQuery({
    queryKey: queryKeys.tasting.places({ limit: 100 }),
    queryFn: () => api.places.list({ limit: 100 }),
    staleTime: 60_000,
  });
}

function useNearby(position: LatLng | null, enabled: boolean) {
  const client = getPlacesClient();
  return useQuery({
    queryKey: queryKeys.tasting.nearby(position),
    queryFn: ({ signal }) => client.nearby(position!, { signal }),
    enabled: enabled && client.enabled && position !== null,
    // Fair use: no automatic retries, no refetch on focus; the client caches for 24 h.
    retry: false,
    refetchOnWindowFocus: false,
    staleTime: 10 * 60_000,
  });
}

function LookupFallback({ error }: { error: unknown }) {
  const { t } = useTranslation();
  const throttled = error instanceof PlacesLookupError && error.reason === 'throttled';
  return (
    <Alert variant="info" data-testid="places-fallback">
      {throttled ? t('tasting.places.throttled') : t('tasting.places.unavailable')}
    </Alert>
  );
}

function CandidateList({
  candidates,
  origin,
  confirmedRefs,
  accuracyM,
  pendingRef,
  onChoose,
}: {
  candidates: OsmPlaceCandidate[];
  origin: LatLng | null;
  confirmedRefs: string[];
  accuracyM?: number | null;
  pendingRef: string | null;
  onChoose: (candidate: OsmPlaceCandidate) => void;
}) {
  const { t } = useTranslation();
  const ranked = useMemo(
    () =>
      origin
        ? rankPlaceCandidates(origin, candidates, { accuracyM, confirmedRefs })
        : candidates.slice(0, 10).map((candidate) => ({
            candidate,
            distanceM: null as number | null,
            previouslyConfirmed: confirmedRefs.includes(candidate.ref),
          })),
    [origin, candidates, accuracyM, confirmedRefs],
  );
  if (!ranked.length)
    return <p className="text-sm text-muted-foreground">{t('tasting.places.none')}</p>;
  return (
    <ul className="divide-y rounded-lg border" data-testid="place-candidates">
      {ranked.map(({ candidate, distanceM, previouslyConfirmed }) => (
        <li key={candidate.ref}>
          <button
            type="button"
            className="flex min-h-[52px] w-full items-center gap-3 px-3 py-2 text-left hover:bg-accent/50 focus-visible:outline-2"
            onClick={() => onChoose(candidate)}
            disabled={pendingRef !== null}
            data-testid="place-candidate"
          >
            <MapPin className="size-5 shrink-0 text-muted-foreground" aria-hidden />
            <span className="min-w-0 flex-1">
              <span className="block truncate font-medium">{candidate.name}</span>
              <span className="block truncate text-xs text-muted-foreground">
                {placeLine(candidate)}
              </span>
            </span>
            {previouslyConfirmed && (
              <Badge variant="info">{t('tasting.places.visitedBefore')}</Badge>
            )}
            {distanceM !== null && (
              <span className="shrink-0 text-xs text-muted-foreground">
                {formatDistance(distanceM)}
              </span>
            )}
            {pendingRef === candidate.ref && <Spinner label={t('app.loading')} />}
          </button>
        </li>
      ))}
    </ul>
  );
}

export interface PlacePickerProps {
  position: LatLng | null;
  accuracyM?: number | null;
  /** True while the device position is still being determined. */
  locating?: boolean;
  onSelect: (place: Place) => void;
}

/**
 * Venue selection: nearby candidates ("Are you at …?"), text search and a map pin. Lookups
 * go from the client straight to OpenStreetMap; only the confirmed venue is stored.
 */
export function PlacePicker({ position, accuracyM, locating, onSelect }: PlacePickerProps) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const client = getPlacesClient();
  const searchId = useId();
  const nameId = useId();
  const [tab, setTab] = useState<'nearby' | 'search' | 'map'>(
    position || locating ? 'nearby' : 'search',
  );
  // The position usually arrives after the first render (late GPS fix, EXIF of a photo added
  // later). Show the nearby suggestions then – unless the user already picked a tab.
  const tabChosen = useRef(false);
  const hasPosition = position !== null;
  useEffect(() => {
    if (!tabChosen.current && (hasPosition || locating)) setTab('nearby');
  }, [hasPosition, locating]);
  const [query, setQuery] = useState('');
  const [submitted, setSubmitted] = useState('');
  const [pin, setPin] = useState<LatLng | null>(null);
  const [manualName, setManualName] = useState('');
  const [pendingRef, setPendingRef] = useState<string | null>(null);

  const myPlaces = useMyPlaces();
  const confirmedRefs = useMemo(
    () => (myPlaces.data?.items ?? []).flatMap((p) => (p.providerRef ? [p.providerRef] : [])),
    [myPlaces.data],
  );
  const nearby = useNearby(position, tab === 'nearby');
  const aroundPin = useNearby(pin, tab === 'map');
  const search = useQuery({
    queryKey: queryKeys.tasting.placeSearch(submitted, position),
    queryFn: ({ signal }) => client.search(submitted, { near: position, signal }),
    enabled: client.enabled && submitted.length >= 2,
    retry: false,
    refetchOnWindowFocus: false,
    staleTime: 10 * 60_000,
  });
  const ownMatches = useMemo(() => {
    const q = submitted.toLowerCase();
    if (!q) return [];
    return (myPlaces.data?.items ?? []).filter((p) => p.name.toLowerCase().includes(q));
  }, [myPlaces.data, submitted]);

  const confirm = useMutation({
    mutationFn: (body: PlaceWriteRequest) => api.places.create(body),
    onSuccess: (place) => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.tasting.placesAll });
      onSelect(place);
    },
    onError: (e) => toastError(e),
    onSettled: () => setPendingRef(null),
  });
  const choose = (candidate: OsmPlaceCandidate) => {
    setPendingRef(candidate.ref);
    confirm.mutate(candidateToRequest(candidate));
  };

  const manual = useMutation({
    mutationFn: async () => {
      if (!pin) throw new Error('No pin');
      // Best effort: the address is optional and a lookup failure must not block saving.
      const address = await client.reverse(pin).catch(() => null);
      return api.places.create({
        provider: 'MANUAL',
        name: manualName.trim(),
        lat: pin.lat,
        lng: pin.lng,
        street: address?.street ?? null,
        postalCode: address?.postalCode ?? null,
        city: address?.city ?? null,
        region: address?.region ?? null,
        countryCode: address?.countryCode ?? null,
      });
    },
    onSuccess: (place) => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.tasting.placesAll });
      onSelect(place);
    },
    onError: (e) => toastError(e),
  });

  const best =
    position && nearby.data?.length
      ? rankPlaceCandidates(position, nearby.data, { accuracyM, confirmedRefs })[0]
      : null;

  return (
    <div data-testid="place-picker">
      <Tabs
        value={tab}
        onValueChange={(v) => {
          tabChosen.current = true;
          setTab(v as typeof tab);
        }}
      >
        <TabsList aria-label={t('tasting.places.pickerLabel')}>
          <TabsTrigger value="nearby">{t('tasting.places.tabNearby')}</TabsTrigger>
          <TabsTrigger value="search">{t('tasting.places.tabSearch')}</TabsTrigger>
          <TabsTrigger value="map">{t('tasting.places.tabMap')}</TabsTrigger>
        </TabsList>

        <TabsContent value="nearby" className="space-y-3">
          {!client.enabled ? (
            <Alert variant="info">{t('tasting.places.disabled')}</Alert>
          ) : !position ? (
            <p className="flex items-center gap-2 text-sm text-muted-foreground" role="status">
              {locating && <Spinner label={t('app.loading')} />}
              {locating ? t('tasting.location.locating') : t('tasting.places.noPosition')}
            </p>
          ) : nearby.isLoading ? (
            <p className="flex items-center gap-2 text-sm" role="status">
              <Spinner label={t('app.loading')} /> {t('tasting.places.searching')}
            </p>
          ) : nearby.error ? (
            <LookupFallback error={nearby.error} />
          ) : (
            <>
              {best && (
                <div
                  className="flex flex-wrap items-center gap-3 rounded-lg border bg-accent/30 p-3"
                  data-testid="place-best"
                >
                  <p className="min-w-0 flex-1 text-sm">
                    {t('tasting.places.areYouAt')}{' '}
                    <strong className="font-semibold">{best.candidate.name}</strong>?
                    <span className="block text-xs text-muted-foreground">
                      {formatDistance(best.distanceM)} · {placeLine(best.candidate)}
                    </span>
                  </p>
                  <Button
                    type="button"
                    loading={pendingRef === best.candidate.ref}
                    onClick={() => choose(best.candidate)}
                    data-testid="place-confirm"
                  >
                    <Check aria-hidden /> {t('tasting.places.confirm')}
                  </Button>
                </div>
              )}
              <CandidateList
                candidates={(nearby.data ?? []).filter((c) => c.ref !== best?.candidate.ref)}
                origin={position}
                accuracyM={accuracyM}
                confirmedRefs={confirmedRefs}
                pendingRef={pendingRef}
                onChoose={choose}
              />
            </>
          )}
        </TabsContent>

        <TabsContent value="search" className="space-y-3">
          {/*
            Search runs on request only – never as autocomplete (Nominatim usage policy). Not a
            <form>: the picker lives inside the capture form and forms must not be nested.
          */}
          <div className="flex gap-2" role="search">
            <div className="min-w-0 flex-1">
              <Label htmlFor={searchId} className="sr-only">
                {t('tasting.places.searchLabel')}
              </Label>
              <Input
                id={searchId}
                type="search"
                enterKeyHint="search"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key !== 'Enter') return;
                  // Enter must not submit the surrounding capture form.
                  e.preventDefault();
                  if (query.trim().length >= 2) setSubmitted(query.trim());
                }}
                placeholder={t('tasting.places.searchPlaceholder')}
                maxLength={200}
                data-testid="place-search-input"
              />
            </div>
            <Button
              type="button"
              variant="outline"
              disabled={query.trim().length < 2}
              onClick={() => setSubmitted(query.trim())}
              data-testid="place-search-submit"
            >
              <Search aria-hidden /> {t('tasting.places.searchAction')}
            </Button>
          </div>
          {ownMatches.length > 0 && (
            <ul className="divide-y rounded-lg border" aria-label={t('tasting.places.mine')}>
              {ownMatches.map((place) => (
                <li key={place.id}>
                  <button
                    type="button"
                    className="flex min-h-[52px] w-full items-center gap-3 px-3 py-2 text-left hover:bg-accent/50"
                    onClick={() => onSelect(place)}
                  >
                    <MapPin className="size-5 shrink-0 text-primary" aria-hidden />
                    <span className="min-w-0 flex-1">
                      <span className="block truncate font-medium">{place.name}</span>
                      <span className="block truncate text-xs text-muted-foreground">
                        {placeLine(place)}
                      </span>
                    </span>
                    <Badge variant="info">{t('tasting.places.visitedBefore')}</Badge>
                  </button>
                </li>
              ))}
            </ul>
          )}
          {search.isFetching && <Spinner label={t('app.loading')} />}
          {search.error ? (
            <LookupFallback error={search.error} />
          ) : (
            search.data && (
              <CandidateList
                candidates={search.data}
                origin={position}
                confirmedRefs={confirmedRefs}
                pendingRef={pendingRef}
                onChoose={choose}
              />
            )
          )}
        </TabsContent>

        <TabsContent value="map" className="space-y-3">
          <p className="text-sm text-muted-foreground">{t('tasting.places.mapHint')}</p>
          <LazyTastingMap
            ariaLabel={t('tasting.places.tabMap')}
            pin={pin}
            onPinChange={setPin}
            center={position}
            className="h-72 w-full overflow-hidden rounded-lg border"
          />
          {pin && (
            <>
              {aroundPin.isLoading && <Spinner label={t('app.loading')} />}
              {aroundPin.error ? (
                <LookupFallback error={aroundPin.error} />
              ) : (
                aroundPin.data && (
                  <CandidateList
                    candidates={aroundPin.data}
                    origin={pin}
                    confirmedRefs={confirmedRefs}
                    pendingRef={pendingRef}
                    onChoose={choose}
                  />
                )
              )}
              <div className="rounded-lg border p-3">
                <Label htmlFor={nameId}>{t('tasting.places.manualLabel')}</Label>
                <p className="text-xs text-muted-foreground">{t('tasting.places.manualHint')}</p>
                <div className="mt-2 flex gap-2">
                  <Input
                    id={nameId}
                    value={manualName}
                    onChange={(e) => setManualName(e.target.value)}
                    maxLength={200}
                    data-testid="place-manual-name"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    disabled={!manualName.trim()}
                    loading={manual.isPending}
                    onClick={() => manual.mutate()}
                    data-testid="place-manual-save"
                  >
                    {t('tasting.places.manualSave')}
                  </Button>
                </div>
              </div>
            </>
          )}
        </TabsContent>
      </Tabs>
      {client.enabled && (
        <p className="mt-2 text-xs text-muted-foreground">{t('tasting.places.attribution')}</p>
      )}
    </div>
  );
}
